import get from 'lodash/get'
import uniqBy from 'lodash/uniqBy'

import type { TrackTextSettings } from 'types/business'
import type { ExplorerUpload } from 'types/explorer'
import type { ZipMode, ZipUrls } from 'types/playlist'
import type {
  AddToDiscoObject,
  PublicPlaylistTrack,
} from 'types/public-playlist'
import type { TempItem } from 'types/shortlist'
import type {
  CachedTrack,
  Track,
  TrackFormatSelection,
  TrackId,
} from 'types/track'
import type { PopoverItemType } from 'types/ui'

import imgixClient from '@disco/api/imgixClient'
import api from '@disco/api/server'

import {
  AUDIO_FILE,
  DRM_FILE,
  IMAGE_FILE,
  PDF_FILE,
  SPREADSHEET_FILE,
  TEXT_FILE,
  UNKNOWN_FILE,
  VIDEO_FILE,
} from '@disco/constants/FileTypes'

import {
  canFormatBeOptimised,
  getFileTypeByExt,
  isFormatBelongsToOptimizedZip,
} from '@disco/utils/file-utils'
import { downloadInFrameIfSupported } from '@disco/utils/target-iframe'
import { formatDuration } from '@disco/utils/time-utils'

export type FormatsSelection = {
  [key: number]: string[]
}

/**
 * Normalizes a track entry to its id. Playlist track arrays hold either a full
 * Track object or a bare numeric id, so callers use this to read an id either way.
 */
/**
 * The subset of the i18next `t` these option builders need.
 *
 * `@disco/utils` deliberately stays free of an i18next dependency: the helpers
 * below run from the `app`, `play` and `cl` bundles, each of which initialises
 * its own instance. The caller passes `t` in rather than this module reaching
 * for one, which also keeps the builders pure and testable.
 *
 * Keys are in the `common` namespace, so callers must bind it.
 */
export type TranslateFn = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string

export const getTrackId = (t: Track | number): TrackId =>
  typeof t === 'number' ? t : t.id

type TrackDownloadLink = {
  ext: string
  url: string
  pretty_size?: string | null | undefined
}

/**
 * Checks if a list of tracks contains only optimized formats.
 *
 * Returns true if all tracks in the list are already in optimized formats
 * (mp3, m4a, mp4) and cannot be further optimized.
 *
 * @param tracks - Array of tracks to check
 * @returns True if all tracks are optimized, false if any can be optimized
 *
 * @example
 * ```typescript
 * const mp3Tracks = [{ all_files: { mp3: {...} } }]
 * const wavTracks = [{ all_files: { wav: {...} } }]
 * listContainsOnlyOptimizedTracks(mp3Tracks) // returns true
 * listContainsOnlyOptimizedTracks(wavTracks) // returns false
 * ```
 */
export const listContainsOnlyOptimizedTracks = (tracks: Track[]): boolean =>
  !tracks
    .flatMap(t => (t.all_files ? Object.keys(t.all_files) : []))
    .some(f => canFormatBeOptimised(f))

/**
 * Gets a list of all original (non-optimized) formats from a track list.
 *
 * Extracts and deduplicates all file formats that can be optimized
 * (formats other than mp3, m4a, mp4).
 *
 * @param tracks - Array of tracks to analyze
 * @returns Array of unique original format extensions
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { all_files: { wav: {...}, mp3: {...} } },
 *   { all_files: { flac: {...}, mp3: {...} } }
 * ]
 * getListOfOriginalFormats(tracks) // returns ['wav', 'flac']
 * ```
 */
export const getListOfOriginalFormats = (tracks: Track[]): string[] => [
  ...new Set(
    tracks
      .flatMap(t => (t.all_files ? Object.keys(t.all_files) : []))
      .filter(f => canFormatBeOptimised(f)),
  ),
]

/**
 * Gets download links for all main file formats of a track.
 *
 * @param track - The track to get download links for
 * @returns Array of download links with extension, URL, and optional size info
 */
const getMainDownloadLinks = (track: Track): TrackDownloadLink[] => {
  const all_files = track.all_files || {}

  const links = Object.keys(all_files)
    .map(ext => {
      const url = all_files[ext]?.play_url
      if (!url) return null
      return {
        ext,
        url,
        pretty_size: track.ext === ext ? track.pretty_size : null,
      }
    })
    .filter((link): link is NonNullable<typeof link> => link !== null)

  return links as TrackDownloadLink[]
}

/**
 * Gets download links for extra downloadable formats of a track.
 *
 * @param track - The track to get extra download links for
 * @returns Array of download links for extra formats
 */
const getExtraDownloadLinks = (track: Track): TrackDownloadLink[] => {
  const formats = track.extra_downloadable_formats || []

  return formats
    .map(extra => {
      return {
        ext: extra.format,
        url: extra.download_url,
      }
    })
    .filter(({ url }) => Boolean(url))
}

/**
 * Gets all available download links for a track (main + extra formats).
 *
 * Combines main file formats and extra downloadable formats into a single array,
 * filtering out any links without valid URLs.
 *
 * @param track - The track to get download links for
 * @returns Array of all available download links
 *
 * @example
 * ```typescript
 * const track = {
 *   all_files: { mp3: { play_url: 'url1' }, wav: { play_url: 'url2' } },
 *   extra_downloadable_formats: [{ format: 'flac', download_url: 'url3' }]
 * }
 * getDownloadLinks(track) // returns [{ ext: 'mp3', url: 'url1' }, ...]
 * ```
 */
export const getDownloadLinks = (track: Track): TrackDownloadLink[] => {
  let links = getMainDownloadLinks(track)

  if (track.extra_downloadable_formats) {
    links = [...links, ...getExtraDownloadLinks(track)]
  }
  return links.filter(({ url }) => Boolean(url))
}

/**
 * Gets the primary download URL for a track.
 *
 * Returns the public download URL, with optional fallback to internal play URL
 * for non-audio/video files when forced.
 *
 * @param track - The track to get download link for
 * @param forceAcquireDocLink - Whether to force acquisition of document links (default: false)
 * @returns The download URL string
 *
 * @example
 * ```typescript
 * getAvailableDownloadLink(track) // returns track.public_download_url2
 * getAvailableDownloadLink(pdfTrack, true) // may return internal_play_url for PDFs
 * ```
 */
export const getAvailableDownloadLink = (
  track: Track,
  forceAcquireDocLink: boolean = false,
): string => {
  const { ext } = track

  const fileType = getFileTypeByExt(ext)
  let url = track.public_download_url2
  if (
    forceAcquireDocLink &&
    !url &&
    fileType !== AUDIO_FILE &&
    fileType !== VIDEO_FILE
  ) {
    url = track.internal_play_url
  }
  return url
}

/**
 * Gets the download link for a track with optional zip mode handling.
 *
 * @param track - The track to get download link for
 * @param forceAcquireDocLink - Whether to force acquisition of document links
 * @param zipMode - Optional zip mode for the download
 * @returns The download URL string
 */
export const getDownloadLink = (
  track: Track,
  forceAcquireDocLink: boolean,
  zipMode?: string,
): string => {
  const url = getAvailableDownloadLink(track, forceAcquireDocLink)

  if (zipMode === 'original_else_optimized') {
    const originalFormat = getOriginalFormatObject(track)
    if (originalFormat) return originalFormat.play_url
  }
  return url
}

/**
 * Gets sorted download links for a track, prioritizing optimized formats.
 *
 * Returns download links sorted so that optimized formats (MP3, M4A, etc.)
 * appear before original (non-optimized) formats (WAV, FLAC, etc.).
 *
 * @param track - The track to get download links for
 * @returns Array of download links sorted by format optimization status
 *
 * @example
 * ```typescript
 * const track = {
 *   all_files: {
 *     'wav': { url: 'track.wav' },  // original
 *     'mp3': { url: 'track.mp3' }   // optimized
 *   }
 * }
 * const links = getSortedDownloadLinks(track)
 * // Returns MP3 link first, then WAV link
 * ```
 */
export const getSortedDownloadLinks = (track: Track): TrackDownloadLink[] =>
  getDownloadLinks(track).sort(
    (a, b) =>
      (canFormatBeOptimised(a.ext) ? 1 : 0) -
      (canFormatBeOptimised(b.ext) ? 1 : 0),
  )

/**
 * Gets sorted download links including extra formats, prioritizing originals.
 *
 * Returns main download links sorted by optimization status, with extra
 * downloadable formats appended if available.
 *
 * @param track - The track to get download links for
 * @returns Array of download links with originals first, then extras
 *
 * @example
 * ```typescript
 * const track = {
 *   all_files: { 'wav': { url: 'track.wav' } },
 *   extra_downloadable_formats: [{ format: 'flac', url: 'track.flac' }]
 * }
 * const links = getSortedFromOriginalDownloadLinks(track)
 * // Returns main formats first, then extra formats
 * ```
 */
export const getSortedFromOriginalDownloadLinks = (
  track: Track,
): TrackDownloadLink[] => {
  const links = getMainDownloadLinks(track)

  let sortedLinks = links.sort(
    (a, b) =>
      (canFormatBeOptimised(b.ext) ? 1 : 0) -
      (canFormatBeOptimised(a.ext) ? 1 : 0),
  )

  if (track.extra_downloadable_formats) {
    sortedLinks = [...sortedLinks, ...getExtraDownloadLinks(track)]
  }

  return sortedLinks
}

/**
 * Creates an object for adding tracks to DISCO.
 *
 * Generates appropriate object structure for adding either a single track
 * or a selection of tracks (including nested tracks) to DISCO.
 *
 * @param track - The main track to add
 * @param nestedTracks - Array of nested tracks (versions/stems)
 * @returns Object formatted for DISCO public API
 *
 * @example
 * ```typescript
 * // Single track
 * const singleObj = getAddToDiscoPublicObject(track, [])
 * // { type: 'TRACK', track: trackObject }
 *
 * // Track with nested versions
 * const multiObj = getAddToDiscoPublicObject(track, nestedTracks)
 * // { type: 'SELECTION', ids: [formatIds...] }
 * ```
 */
export const getAddToDiscoPublicObject = (
  track: Track,
  nestedTracks: Track[],
): AddToDiscoObject => {
  let object = { type: 'TRACK', track }

  if (nestedTracks) {
    const nestedTracksIds = getAllFormatIds(nestedTracks)
    const mainTrackIds = getAllFormatIds([track])
    object = {
      type: 'SELECTION',
      // @ts-expect-error todo fix later
      ids: [...mainTrackIds, ...nestedTracksIds],
    }
  }

  // @ts-expect-error todo fix later
  return object
}

/**
 * Generates download options for a track with alternative formats.
 *
 * Creates a menu of download options including the current format and
 * alternative formats, with special handling for nested tracks.
 *
 * @param t - Translate function bound to the `common` namespace
 * @param track - The track to generate download options for
 * @param ext - Current file extension being displayed
 * @param url - Current download URL
 * @param onClick - Optional callback for tracking download events
 * @returns Array of popover menu items for download options
 *
 * @example
 * ```typescript
 * const options = getDownloadOptions(t, track, 'mp3', 'track.mp3', (ext) => {
 *   console.log(`Downloaded ${ext} format`)
 * })
 * // Returns menu items for different format downloads
 * ```
 */
export const getDownloadOptions = (
  t: TranslateFn,
  track: Track,
  ext: string,
  url: string,
  onClick?: (ext?: string | null | undefined) => void,
): PopoverItemType[] => {
  const links: TrackDownloadLink[] = getDownloadLinks(track).filter(
    f => f.ext !== ext,
  )

  const nestedTracksCount: number = getNestedTracksCount(track)
  const target = downloadInFrameIfSupported()

  let options: PopoverItemType[] = []
  if (url) {
    options.push({
      label:
        nestedTracksCount > 0
          ? t('common:downloadMainFormat', 'Download main {{format}}', {
              format: ext.toUpperCase(),
            })
          : t('common:downloadFormat', 'Download {{format}}', {
              format: ext.toUpperCase(),
            }),
      type: 'link' as const,
      link: {
        url,
        download: true,
        target: target ?? undefined,
      },
      onClick: () => onClick && onClick(ext),
    })
  }

  options = [
    ...options,
    ...links.map((link: TrackDownloadLink) => ({
      label:
        nestedTracksCount > 0
          ? t('common:downloadMainFormat', 'Download main {{format}}', {
              format: link.ext.toUpperCase(),
            })
          : t('common:downloadFormat', 'Download {{format}}', {
              format: link.ext.toUpperCase(),
            }),
      type: 'link' as const,
      link: {
        url: link.url,
        download: true,
        target: target ?? undefined,
      },
      onClick: () => onClick && onClick(link.ext),
    })),
  ]

  const nestingZipUrls = getZipDownloadsLinks(track.nesting_zip_urls, 'public')

  if (nestedTracksCount > 0 && nestingZipUrls.length !== 0) {
    options.push({
      label: t('common:downloadAll', 'Download all'),
      nested: nestingZipUrls.map(link => ({
        label: link.title,
        type: 'link',
        link: {
          url: link.url,
          download: true,
          target: target ?? undefined,
        },
      })),
    })
  }

  return options
}

/**
 * Generates save-to-DISCO options for tracks.
 *
 * Creates menu options for saving tracks to DISCO, with different options
 * for single tracks vs. tracks with nested versions.
 *
 * @param t - Translate function bound to the `common` namespace
 * @param track - The main track to save
 * @param nestedTracks - Array of nested tracks (versions/stems)
 * @param onClick - Callback function for save operations
 * @returns Array of popover menu items for save options
 *
 * @example
 * ```typescript
 * const options = getSaveToDiscoOptions(t, track, nestedTracks, (tracks) => {
 *   saveToDiscoAction(tracks)
 * })
 * // Returns options like "Save main version" or "Save all versions"
 * ```
 */
export const getSaveToDiscoOptions = (
  t: TranslateFn,
  track: Track,
  nestedTracks: Track[],
  onClick: (nestedTracks: Track[]) => void,
): PopoverItemType[] => {
  let options

  if (nestedTracks.length > 0) {
    options = [
      {
        label: t('common:saveMainVersionToDisco', 'Save main version to DISCO'),
        onClick,
      },
      {
        label: t('common:saveAllVersionsToDisco', 'Save all versions to DISCO'),
        onClick: () => onClick(nestedTracks),
      },
    ]
  } else {
    options = [
      {
        label: t('common:saveToDisco', 'Save to DISCO'),
        onClick,
      },
    ]
  }

  return options
}

/**
 * Checks if an array of tracks contains mixed file types.
 *
 * Determines whether the tracks contain both audio and video files,
 * which may require special handling in the UI.
 *
 * @param tracks - Array of tracks to check
 * @returns True if tracks contain both audio and video files
 *
 * @example
 * ```typescript
 * const mixedTracks = [
 *   { ext: 'mp3' },  // audio
 *   { ext: 'mp4' }   // video
 * ]
 * areTrackTypesMixed(mixedTracks) // returns true
 *
 * const audioTracks = [{ ext: 'mp3' }, { ext: 'wav' }]
 * areTrackTypesMixed(audioTracks) // returns false
 * ```
 */
export const areTrackTypesMixed = (tracks: Track[]): boolean => {
  const trackTypes = [...new Set(tracks.map(t => getFileTypeByExt(t.ext)))]
  return (
    trackTypes.length > 1 &&
    trackTypes.some(type => type === VIDEO_FILE || type === AUDIO_FILE)
  )
}

/**
 * Gets the original (non-optimized) format of a track.
 *
 * Returns the first available format that can be optimized (e.g., WAV, FLAC)
 * rather than already optimized formats like MP3.
 *
 * @param track - The track to get original format for
 * @returns The original format extension or null if none found
 *
 * @example
 * ```typescript
 * const track = { all_files: { wav: {...}, mp3: {...} } }
 * getOriginalFormat(track) // 'wav'
 *
 * const optimizedTrack = { all_files: { mp3: {...} } }
 * getOriginalFormat(optimizedTrack) // null
 * ```
 */
export function getOriginalFormat(track: Track): string | null | undefined {
  return track.all_files
    ? Object.keys(track.all_files).find(f => canFormatBeOptimised(f))
    : null
}

/**
 * Gets the original format object with full metadata.
 *
 * Returns the complete format object (including URLs, metadata) for the
 * original (non-optimized) format of a track.
 *
 * @param track - The track to get original format object for
 * @returns The original format object or null if none found
 */
export function getOriginalFormatObject(track: Track): any | null | undefined {
  const format = getOriginalFormat(track)
  return format && track.all_files ? track.all_files[format] : null
}

/**
 * Gets the optimized format of a track.
 *
 * Returns the first available optimized format (mp3, m4a, mp4) from the track.
 * Optimized formats are smaller and more compatible for web playback.
 *
 * @param track - The track to get optimized format for
 * @returns The optimized format extension or null if none found
 *
 * @example
 * ```typescript
 * const track = { all_files: { wav: {...}, mp3: {...} } }
 * getOptimizedFormat(track) // 'mp3'
 *
 * const unoptimizedTrack = { all_files: { wav: {...} } }
 * getOptimizedFormat(unoptimizedTrack) // null
 * ```
 */
export function getOptimizedFormat(track: Track): string | null | undefined {
  return track.all_files
    ? Object.keys(track.all_files).find(f => !canFormatBeOptimised(f))
    : null
}

/**
 * Gets the optimized format object with track file ID and format.
 *
 * Returns a TrackFormatSelection object for the optimized format of a track,
 * containing the track file ID and format extension.
 *
 * @param track - The track to get optimized format object for
 * @returns TrackFormatSelection object or null if no optimized format found
 *
 * @example
 * ```typescript
 * const track = { all_files: { wav: {...}, mp3: { id: '123' } } }
 * getOptimisedFormatObject(track)
 * // { track_file_id: '123', format: 'mp3' }
 * ```
 */
export function getOptimisedFormatObject(
  track: Track,
): TrackFormatSelection | null | undefined {
  const format = getOptimizedFormat(track)
  const all_files = track.all_files
  return format && all_files
    ? {
        track_file_id: all_files[format]?.id,
        format: format,
      }
    : null
}

/**
 * Gets the first extra downloadable format from a track.
 *
 * Returns the format extension of the first extra downloadable format
 * if available. Extra formats are additional formats beyond the main track files.
 *
 * @param track - The track to get extra format for
 * @returns The extra format extension or null if none available
 *
 * @example
 * ```typescript
 * const track = {
 *   extra_downloadable_formats: [{ format: 'flac' }, { format: 'aiff' }]
 * }
 * getExtraFormat(track) // 'flac'
 * ```
 */
export function getExtraFormat(track: Track): string | null | undefined {
  const obj =
    track.extra_downloadable_formats && track.extra_downloadable_formats[0]
  return obj ? obj.format : null
}

/**
 * Gets the format that will be included in original zip downloads.
 *
 * Returns the format that would be included when downloading the track
 * in a zip with mode=original (non-optimized formats).
 *
 * @param track - The track to get original zip format for
 * @returns The format extension for original zip or null if none found
 *
 * @example
 * ```typescript
 * const track = { all_files: { wav: {...}, mp3: {...} } }
 * getOriginalZipFormat(track) // 'wav' - included in original zip
 * ```
 */
export function getOriginalZipFormat(track: Track): string | null | undefined {
  // if format will be put into `zip_stream/?mode=original`
  return track.all_files
    ? Object.keys(track.all_files).find(f => !isFormatBelongsToOptimizedZip(f))
    : null
}

/**
 * Gets the format that will be included in optimized zip downloads.
 *
 * Returns the format that would be included when downloading the track
 * in a zip with mode=optimized (optimized formats like mp3, m4a, mp4).
 *
 * @param track - The track to get optimized zip format for
 * @returns The format extension for optimized zip or null if none found
 *
 * @example
 * ```typescript
 * const track = { all_files: { wav: {...}, mp3: {...} } }
 * getOptimizedZipFormat(track) // 'mp3' - included in optimized zip
 * ```
 */
export function getOptimizedZipFormat(track: Track): string | null | undefined {
  // if format will be put into `zip_stream/?mode=optimized`
  return track.all_files
    ? Object.keys(track.all_files).find(f => isFormatBelongsToOptimizedZip(f))
    : null
}

/**
 * Gets optimized format IDs for a collection of tracks.
 *
 * Returns TrackFormatSelection objects for the optimized formats of tracks,
 * preferring optimized formats (mp3, m4a, mp4) over original formats.
 *
 * @param tracks - Array of tracks to get optimized format IDs for
 * @returns Array of TrackFormatSelection objects for optimized formats
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { all_files: { wav: { id: '1' }, mp3: { id: '2' } } },
 *   { all_files: { flac: { id: '3' } } }
 * ]
 * getOptimizedFormatIds(tracks)
 * // [{ track_file_id: '2', format: 'mp3' }, { track_file_id: '3', format: 'flac' }]
 * ```
 */
export function getOptimizedFormatIds(
  tracks: PublicPlaylistTrack[],
): TrackFormatSelection[] {
  if (!tracks || tracks.length === 0) return []

  const result = tracks
    .map(track => {
      if (!track.all_files || Object.keys(track.all_files).length === 0)
        return null

      const formatKeys = Object.keys(track.all_files)

      let format = formatKeys.find(f => !canFormatBeOptimised(f))
      if (!format) format = formatKeys[0]
      if (!format) return null
      const id = track.all_files?.[format]?.id
      if (!id) return null
      return { track_file_id: id, format: format }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  return result as TrackFormatSelection[]
}

/**
 * Gets original format IDs, falling back to optimized if no original exists.
 *
 * Returns TrackFormatSelection objects preferring original (non-optimized) formats
 * but falling back to optimized formats if original formats are not available.
 *
 * @param tracks - Array of tracks to get format IDs for
 * @returns Array of TrackFormatSelection objects for original or optimized formats
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { all_files: { wav: { id: '1' }, mp3: { id: '2' } } }, // Original available
 *   { all_files: { mp3: { id: '3' } } } // Only optimized available
 * ]
 * getOriginalElseOptimizedFormatIds(tracks)
 * // [{ track_file_id: '1', format: 'wav' }, { track_file_id: '3', format: 'mp3' }]
 * ```
 */
export function getOriginalElseOptimizedFormatIds(
  tracks: PublicPlaylistTrack[],
): TrackFormatSelection[] {
  if (!tracks || tracks.length === 0) return []

  const result = tracks
    .map(track => {
      if (!track.all_files || Object.keys(track.all_files).length === 0)
        return null

      const formatKeys = Object.keys(track.all_files)

      let format = formatKeys.find(f => canFormatBeOptimised(f))
      if (!format) format = formatKeys[0]
      if (!format) return null
      const id = track.all_files?.[format]?.id
      if (!id) return null
      return { track_file_id: id, format: format }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  return result as TrackFormatSelection[]
}

/**
 * Gets all format IDs for all formats of all tracks.
 *
 * Returns TrackFormatSelection objects for every format available across
 * all tracks, including both main files and extra downloadable formats.
 *
 * @param tracks - Array of tracks to get all format IDs for
 * @returns Array of TrackFormatSelection objects for all formats
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { all_files: { wav: { id: '1' }, mp3: { id: '2' } } },
 *   { all_files: { flac: { id: '3' }, m4a: { id: '4' } } }
 * ]
 * getAllFormatIds(tracks)
 * // [
 * //   { track_file_id: '1', format: 'wav' },
 * //   { track_file_id: '2', format: 'mp3' },
 * //   { track_file_id: '3', format: 'flac' },
 * //   { track_file_id: '4', format: 'm4a' }
 * // ]
 * ```
 */
export function getAllFormatIds(
  tracks: Track[] | PublicPlaylistTrack[],
): TrackFormatSelection[] {
  if (!tracks || tracks.length === 0) return []

  const trackFormatsArrays = tracks
    .map(track => {
      if (!track.all_files || Object.keys(track.all_files).length === 0)
        return null

      const formatKeys = Object.keys(track.all_files)
      const formatIds = formatKeys
        .map(format => {
          const id = track.all_files?.[format]?.id
          if (!id) return null
          return { track_file_id: id, format: format }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)

      return formatIds as TrackFormatSelection[]
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  return trackFormatsArrays.flat()
}

/**
 * Includes optimized formats for tracks that have original formats selected.
 *
 * When original formats are selected, this function automatically includes
 * the corresponding optimized formats to provide users with download options.
 *
 * @param tracks - Array of tracks to check for optimized formats
 * @param formatIds - Currently selected format IDs
 * @returns Expanded array including both original and optimized format IDs
 *
 * @example
 * ```typescript
 * const tracks = [{ all_files: { wav: { id: '1' }, mp3: { id: '2' } } }]
 * const selectedIds = [{ track_file_id: '1', format: 'wav' }]
 * const expandedIds = includeOptimisedFormats(tracks, selectedIds)
 * // Includes both WAV (original) and MP3 (optimized) format IDs
 * ```
 */
export function includeOptimisedFormats(
  tracks: PublicPlaylistTrack[],
  formatIds: TrackFormatSelection[],
): TrackFormatSelection[] {
  const resultIds = [...formatIds]
  tracks.forEach(track => {
    if (track.all_files && Object.keys(track.all_files).length > 1) {
      const originalFormat = getOriginalFormatObject(track)
      if (
        originalFormat &&
        formatIds.find(f => f.track_file_id === originalFormat.id)
      ) {
        const optimisedFormat = getOptimisedFormatObject(track)
        if (
          optimisedFormat &&
          !formatIds.find(
            f => f.track_file_id === optimisedFormat.track_file_id,
          )
        ) {
          resultIds.push(optimisedFormat)
        }
      }
    }
  })
  return resultIds
}

/**
 * Gets the download/play URL for a specific format across tracks.
 *
 * Searches through tracks to find the specified format and returns its
 * play URL or download URL. Checks both main files and extra formats.
 *
 * @param tracks - Array of tracks to search through
 * @param formatId - The specific format ID to find the link for
 * @returns The URL for the format, or null if not found
 *
 * @example
 * ```typescript
 * const tracks = [{ all_files: { mp3: { id: '123', play_url: 'url' } } }]
 * const formatId = { track_file_id: '123', format: 'mp3' }
 * const url = getFormatLink(tracks, formatId) // 'url'
 * ```
 */
export const getFormatLink = (
  tracks: PublicPlaylistTrack[],
  formatId: TrackFormatSelection,
): string | null | undefined => {
  for (const track of tracks) {
    const { all_files, extra_downloadable_formats } = track

    const selectedId = formatId
    if (all_files) {
      const format = all_files[selectedId.format]
      if (format && format.id === selectedId.track_file_id)
        return format.play_url
    }
    if (extra_downloadable_formats) {
      const extra = extra_downloadable_formats.find(
        e => e.format === selectedId.format,
      )
      if (extra && extra.track_file_id === selectedId.track_file_id) {
        return extra.download_url
      }
    }
  }

  return null
}

/**
 * Converts user format selections to TrackFormatSelection objects.
 *
 * Takes a FormatsSelection object (mapping track IDs to format arrays) and
 * converts it to TrackFormatSelection objects with track file IDs.
 *
 * @param tracks - Array of tracks containing format information
 * @param selectedFormats - User's format selections by track ID
 * @returns Array of TrackFormatSelection objects for selected formats
 *
 * @example
 * ```typescript
 * const tracks = [{ id: 1, all_files: { mp3: { id: '123' } } }]
 * const selections = { 1: ['mp3'] }
 * const formatIds = getSelectedFormatIds(tracks, selections)
 * // [{ track_file_id: '123', format: 'mp3' }]
 * ```
 */
export const getSelectedFormatIds = (
  tracks: PublicPlaylistTrack[],
  selectedFormats?: FormatsSelection | null,
): TrackFormatSelection[] => {
  if (!selectedFormats || !tracks) return []
  const formatIds: Array<TrackFormatSelection> = []
  tracks.forEach(track => {
    const selectedTrackFormats = selectedFormats[track.id]
    if (selectedTrackFormats) {
      selectedTrackFormats.forEach(selectedFmt => {
        if (!track.all_files) return
        const format = track.all_files[selectedFmt]
        let formatId
        if (format) {
          formatId = { track_file_id: format.id, format: selectedFmt }
        }
        if (!formatId && track.extra_downloadable_formats) {
          const extra = track.extra_downloadable_formats.find(
            e => e.format === selectedFmt,
          )
          if (extra) {
            formatId = {
              track_file_id: extra.track_file_id,
              format: selectedFmt,
            }
          }
        }
        if (formatId) {
          formatIds.push(formatId)
        }
      })
    }
  })
  return formatIds
}

/**
 * Generates a subheader text for track display.
 *
 * Creates appropriate subheader text based on track metadata, text settings,
 * and file type. Handles different display modes and video caption formatting.
 *
 * @param track - Track object with artist, album, and extension
 * @param shortVideoCaption - Whether to use short caption for videos
 * @param textSettings - Text display settings for metadata formatting
 * @returns Formatted subheader string for the track
 *
 * @example
 * ```typescript
 * const track = { artist: 'Artist Name', album: 'Album Title', ext: 'mp3' }
 * getTrackSubheader(track) // 'Artist Name: Album Title'
 *
 * const videoTrack = { artist: 'Creator', album: 'Video', ext: 'mp4' }
 * getTrackSubheader(videoTrack, true) // 'Creator' (short caption)
 * ```
 */
export const getTrackSubheader = (
  track:
    | Track
    | {
        artist: string | null | undefined
        album: string | null | undefined
        ext: string | null | undefined
      },
  shortVideoCaption?: boolean | null,
  textSettings?: TrackTextSettings,
): string => {
  const { artist, album, ext } = track

  let subheader = `${artist || ''}: ${album || ''}`
  // @ts-expect-error todo fix later
  if (textSettings === 'title_composer_description' && track.metadata) {
    subheader = `${get(track, 'metadata.Composer', '')}: ${get(
      track,
      'metadata.Description',
      '',
    )}`
  } else if (shortVideoCaption && getFileTypeByExt(ext) === VIDEO_FILE) {
    subheader = `${artist || ''}`
  }

  return subheader.replace(/(^:\s+)|(:\s+$)/, '')
}

/**
 * Gets detailed status information for a track.
 *
 * Returns comprehensive status details including playability, preview availability,
 * watermarking status, and formatted subheader text.
 *
 * @param track - The track to get status details for
 * @param options - Configuration options for status calculation
 * @param options.showWatermarking - Whether to include watermarking status
 * @param options.textSettings - Text display settings for metadata formatting
 * @returns Object containing detailed track status information
 *
 * @example
 * ```typescript
 * const track = { ext: 'mp3', artist: 'Artist', album: 'Album' }
 * const details = getTrackStatusDetails(track, { showWatermarking: true })
 * // Returns: { isTemp: false, isOpenable: true, isPlayable: true, ... }
 * ```
 */
export const getTrackStatusDetails = (
  track: Track,
  options: {
    showWatermarking?: boolean
    textSettings?: TrackTextSettings
  } = {},
): {
  isTemp: boolean
  isOpenable: boolean
  isPlayable: boolean
  isPreviewable: boolean
  subheader: string
  fileType: string
} => {
  let isTemp = true // most states are "isTemp = true"

  // DEPRECATED: `isOpenable` - instead use `isPlayable` / `isPreviewable``
  let isOpenable = false // most states are "isOpenable = false"

  let isPlayable = false
  let isPreviewable = false
  let line2 = ''

  const { ext, virus } = track
  const fileType = getFileTypeByExt(ext)

  if (!track.id) {
    if (track.importing) {
      line2 = 'Importing…'
    } else if (track.progress < 100) {
      // upload step #1: upload
      line2 = `Uploading ${Math.floor(track.progress)}%`
    } else {
      // upload step #2: processing
      line2 = 'Processing...'
    }
  } else {
    if (virus) {
      isTemp = false
      line2 = 'Virus detected'
    } else if (!track.uploaded && !track.client_library_url) {
      // upload step #3: processing
      line2 = 'Processing...'
    } else if (fileType === DRM_FILE) {
      isTemp = false
      line2 = 'Playing this track isn’t supported'
    } else if (fileType === AUDIO_FILE || fileType === VIDEO_FILE) {
      // on fresh uploads `transcode_pending` is undefined, thus check for `false`
      if (track.transcode_pending) {
        // upload step #4: transcoding
        line2 = 'Transcoding...'
      } else {
        // upload step #5: playable track
        isTemp = false
        isOpenable = true
        isPlayable = true
        line2 = getTrackSubheader(track, false, options?.textSettings)
      }
    } else {
      if (track.preview_pending) {
        // upload step #4: preview
        line2 = 'Generating preview...'
      } else if (track.previews && track.previews.length > 0) {
        // upload step #5: preview ready
        isTemp = false
        isOpenable = true
        isPreviewable = true
        line2 = ext ? `${ext.toUpperCase()} file` : 'Unknown file'
      } else {
        // upload step #5: preview failed
        isTemp = false
        line2 = ext ? `${ext.toUpperCase()} file` : 'Unknown file'
      }
    }
  }
  return {
    isTemp,
    isOpenable,
    isPlayable,
    isPreviewable,
    subheader: line2,
    fileType,
  }
}

/**
 * Gets the artwork URL for a track.
 *
 * Returns the appropriate artwork URL for the track, with optional size specification.
 * Handles both regular tracks and cached tracks.
 *
 * @param track - The track to get artwork for
 * @param size - Optional size specification ('160' for thumbnail, 'full' for full size)
 * @returns The artwork URL, or null if no artwork available
 *
 * @example
 * ```typescript
 * const track = { artwork_url: 'https://example.com/artwork.jpg' }
 * getTrackArtwork(track) // 'https://example.com/artwork.jpg'
 * getTrackArtwork(track, '160') // thumbnail version
 * getTrackArtwork(track, 'full') // full size version
 * ```
 */
export const getTrackArtwork = (
  track: Track | CachedTrack,
  size?: '160' | 'full',
): string | null | undefined => {
  let artwork
  const artworkFull = get(track, 'artwork_sizes[full]', track.artwork)
  const artworkThumb = get(track, 'artwork_sizes[160x160]', track.artwork)
  if (size === 'full') {
    artwork = artworkFull
  } else {
    artwork = artworkThumb
    if (
      // TIFF patch for https://disco-team.atlassian.net/browse/DISCO-7851
      // "artwork_sizes": {"160x160": "", "full": "...tif"}
      !artworkThumb &&
      artworkFull &&
      (artworkFull.endsWith('.tif') || artworkFull.endsWith('.tiff'))
    ) {
      artwork = artworkFull
    }
  }

  let result =
    !artwork && track?.previews?.[0]?.thumb
      ? get(track, 'previews[0].thumb', null)
      : artwork?.artwork_sizes?.['160x160'] ?? artwork

  if (result && (result.endsWith('.tiff') || result.endsWith('.tif'))) {
    // TIFF patch for https://disco-team.atlassian.net/browse/DISCO-7851
    result = imgixClient.buildURL(result, { auto: 'format' })
  }

  return result
}

const fileTypeCampaignArtwork = {
  [VIDEO_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/video.png',
  [AUDIO_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/track.png',
  [IMAGE_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/image-file.png',
  [TEXT_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/text-file.png',
  [PDF_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/pdf-file.png',
  [SPREADSHEET_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/spreadsheet-file.png',
  [UNKNOWN_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/unknown-file.png',
  [DRM_FILE]:
    'https://static.disco.ac/campaign-editor/fileicons-r1/unknown-file.png',
  virus: 'https://static.disco.ac/campaign-editor/fileicons-r1/bug-file.png',
} as const

/**
 * Gets the artwork URL for a campaign track.
 *
 * Returns the appropriate artwork URL for tracks used in campaigns,
 * with fallback logic specific to campaign contexts.
 *
 * @param track - The campaign track to get artwork for
 * @returns The campaign artwork URL, or null if no artwork available
 */
export const getCampaignTrackArtwork = (
  track: Track,
): string | null | undefined => {
  if (track.virus) return fileTypeCampaignArtwork['virus']
  const artwork = getTrackArtwork(track)
  if (artwork) return artwork
  const fileType = getFileTypeByExt(track.ext)
  const defaultImage = fileTypeCampaignArtwork[fileType]
  return defaultImage || null
}

/**
 * Gets all available format extensions for a track.
 *
 * Returns an array of all format extensions available for the track,
 * including both main files and extra downloadable formats.
 *
 * @param track - The track to get formats for
 * @returns Array of format extensions available for the track
 *
 * @example
 * ```typescript
 * const track = {
 *   all_files: { wav: {...}, mp3: {...} },
 *   extra_downloadable_formats: [{ format: 'flac' }]
 * }
 * getTrackFormats(track) // ['wav', 'mp3', 'flac']
 * ```
 */
export const getTrackFormats = (track: Track): string[] => {
  if (!track.all_files) return []
  return Object.keys(track.all_files)
}

/**
 * Checks if all available formats are selected for all tracks.
 *
 * Determines whether the current selection includes all available formats
 * for every track in the collection.
 *
 * @param tracks - Array of tracks to check format selection for
 * @param selection - Current format selection by track ID
 * @returns True if all formats are selected for all tracks
 *
 * @example
 * ```typescript
 * const tracks = [{ id: 1, all_files: { mp3: {}, wav: {} } }]
 * const allSelected = { 1: ['mp3', 'wav'] }
 * const partial = { 1: ['mp3'] }
 *
 * areAllFormatsSelected(tracks, allSelected) // true
 * areAllFormatsSelected(tracks, partial) // false
 * ```
 */
export const areAllFormatsSelected = (
  tracks: PublicPlaylistTrack[],
  selection: FormatsSelection,
): boolean => {
  return tracks.every(t => {
    const selectedFormats = selection[t.id] || []
    return t.formatColumns.filter(Boolean).length === selectedFormats.length
  })
}

/**
 * Checks if all formats are selected for tracks in a specific column.
 *
 * Used in table/grid views to determine if a column's format selection
 * checkbox should be checked (all formats selected for that column).
 *
 * @param tracks - Array of tracks to check
 * @param selection - Current format selection by track ID
 * @param columnIdx - Index of the column to check
 * @returns True if all formats are selected for the column
 *
 * @example
 * ```typescript
 * const tracks = [{ id: 1, all_files: { mp3: {}, wav: {} } }]
 * const selection = { 1: ['mp3', 'wav'] }
 * areAllColumnFormatsSelected(tracks, selection, 0) // true
 * ```
 */
export const areAllColumnFormatsSelected = (
  tracks: PublicPlaylistTrack[],
  selection: FormatsSelection,
  columnIdx: number,
): boolean => {
  return tracks.every(t => {
    const selectedFormats = selection[t.id] || []
    const format = t.formatColumns[columnIdx]
    if (!format) return true
    // @ts-expect-error todo fix later
    return selectedFormats.includes(format)
  })
}

/**
 * Checks if any tracks in a column have selectable formats.
 *
 * Determines whether there are any format options available for tracks
 * in a specific column position, used for UI state management.
 *
 * @param tracks - Array of tracks to check
 * @param columnIdx - Index of the column to check for formats
 * @returns True if any tracks in the column have formats available
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, all_files: { mp3: {} } },
 *   { id: 2, all_files: null }
 * ]
 * areColumnFormatsPresent(tracks, 0) // true - first track has formats
 * ```
 */
export const areColumnFormatsPresent = (
  tracks: PublicPlaylistTrack[],
  columnIdx: number,
): boolean => {
  return tracks.some(t => {
    return Boolean(t.formatColumns[columnIdx])
  })
}

/**
 * Checks if a track can be played in the application.
 *
 * Determines whether a track is in a playable format (audio or video)
 * and not in a temporary processing state.
 *
 * @param track - The track to check for playability
 * @returns True if the track can be played, false otherwise
 *
 * @example
 * ```typescript
 * const audioTrack = { ext: 'mp3', temp: false }
 * const pdfFile = { ext: 'pdf', temp: false }
 * const tempTrack = { ext: 'mp3', temp: true }
 *
 * isPlayableTrack(audioTrack) // true
 * isPlayableTrack(pdfFile) // false
 * isPlayableTrack(tempTrack) // false
 * ```
 */
export const isPlayableTrack = (track: Track | CachedTrack): boolean => {
  const fileType = getFileTypeByExt(track.ext)
  const result =
    (fileType === AUDIO_FILE || fileType === VIDEO_FILE) &&
    !track.outside_your_territories
  return result
}

/**
 * Checks if a track is in read-only mode.
 *
 * Determines whether a track can be modified by checking its read-only status.
 * Read-only tracks typically cannot be edited, deleted, or have their metadata changed.
 *
 * @param track - The track to check for read-only status
 * @returns True if the track is read-only, false otherwise
 *
 * @example
 * ```typescript
 * const readOnlyTrack = { read_only: true }
 * const editableTrack = { read_only: false }
 *
 * isReadOnlyTrack(readOnlyTrack) // true
 * isReadOnlyTrack(editableTrack) // false
 * isReadOnlyTrack(null) // false
 * ```
 */
export const isReadOnlyTrack = (track?: Track | null): boolean => {
  return track?.read_only || false
}

/**
 * Checks if a track is from an aggregated search result.
 *
 * Determines whether a track comes from aggregated search results,
 * which may have different display and interaction behaviors.
 *
 * @param track - The track to check for aggregated search status
 * @returns True if the track is from aggregated search results
 *
 * @example
 * ```typescript
 * const aggregatedTrack = { aggregated_search: true }
 * const regularTrack = { aggregated_search: false }
 *
 * isAggregatedSearchTrack(aggregatedTrack) // true
 * isAggregatedSearchTrack(regularTrack) // false
 * ```
 */
export const isAggregatedSearchTrack = (track?: Track | null): boolean => {
  return track?.internal_data?.is_aggregated_search_track || false
}

/**
 * Whether a track is a saved audio file, as opposed to an in-progress upload
 * or a document, image or video. Only these can seed similarity searches.
 */
export const isPersistedAudioTrack = (
  track: Track | null | undefined,
): track is Track =>
  Boolean(
    track &&
      track.id > 0 &&
      !track.tempId &&
      getFileTypeByExt(track.ext) === AUDIO_FILE,
  )

/**
 * Gets the count of versions for a track.
 *
 * Returns the number of versions (alternative recordings) associated with a track.
 * Versions are different recordings of the same composition.
 *
 * @param track - The track to get version count for
 * @returns Number of versions, or 0 if none or track is null
 *
 * @example
 * ```typescript
 * const trackWithVersions = { versions_count: 3 }
 * const trackWithoutVersions = { versions_count: 0 }
 *
 * getVersionsCount(trackWithVersions) // 3
 * getVersionsCount(trackWithoutVersions) // 0
 * getVersionsCount(null) // 0
 * ```
 */
export const getVersionsCount = (track?: Track | null): number => {
  return track?.nested_version_count || 0
}

/**
 * Gets the count of stems for a track.
 *
 * Returns the number of stems (individual instrument/vocal tracks) associated with a track.
 * Stems are separate audio channels that can be mixed together.
 *
 * @param track - The track to get stem count for
 * @returns Number of stems, or 0 if none or track is null
 *
 * @example
 * ```typescript
 * const trackWithStems = { stems_count: 8 }
 * const trackWithoutStems = { stems_count: 0 }
 *
 * getStemsCount(trackWithStems) // 8
 * getStemsCount(trackWithoutStems) // 0
 * getStemsCount(null) // 0
 * ```
 */
export const getStemsCount = (track?: Track | null): number => {
  return track?.nested_stem_count || 0
}

/**
 * Gets the total count of nested tracks (versions + stems).
 *
 * Returns the combined count of all nested tracks including both versions
 * and stems associated with the main track.
 *
 * @param track - The track to get nested track count for
 * @returns Total number of nested tracks (versions + stems)
 *
 * @example
 * ```typescript
 * const track = { nested_version_count: 2, nested_stem_count: 6 }
 * getNestedTracksCount(track) // 8 (2 versions + 6 stems)
 *
 * const simpleTrack = { nested_version_count: 0, nested_stem_count: 0 }
 * getNestedTracksCount(simpleTrack) // 0
 * ```
 */
export const getNestedTracksCount = (track?: Track | null): number => {
  // TODO: enable nesting
  // https://disco-team.atlassian.net/browse/DISCO-5771
  if (isAggregatedSearchTrack(track)) {
    return 0
  }

  const versions = getVersionsCount(track)
  const stems = getStemsCount(track)

  return versions + (stems > 0 ? 1 : 0)
}

/**
 * Filters tracks to only include those with nested tracks (versions/stems).
 *
 * Returns only tracks that have associated nested tracks, useful for
 * displaying tracks with additional content or complex structures.
 *
 * @param tracks - Array of tracks to filter
 * @returns Array containing only tracks with nested tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, nested_version_count: 2 },
 *   { id: 2, nested_version_count: 0 },
 *   { id: 3, nested_stem_count: 4 }
 * ]
 * getTracksWithNestedTracks(tracks) // [track 1, track 3]
 * ```
 */
export const getTracksWithNestedTracks = (
  tracks: PublicPlaylistTrack[] = [],
): PublicPlaylistTrack[] => {
  const result = tracks.flatMap(t => [t].concat(t.nested_tracks || []))

  return uniqBy(result, 'id')
}

/**
 * Gets the total count of versions across an array of tracks.
 *
 * Sums up the version counts from all tracks in the array to get
 * the total number of versions available.
 *
 * @param tracks - Array of tracks to count versions for
 * @returns Total number of versions across all tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { nested_version_count: 2 },
 *   { nested_version_count: 3 },
 *   { nested_version_count: 0 }
 * ]
 * getVersionsCountFromTracksArray(tracks) // 5
 * ```
 */
export const getVersionsCountFromTracksArray = (
  tracks?: Track[] | null,
): number => {
  const versionsCount = (tracks || []).reduce(
    (versionsCount, track) => versionsCount + getVersionsCount(track),
    0,
  )

  return versionsCount
}

/**
 * Gets the total count of stems across an array of tracks.
 *
 * Sums up the stem counts from all tracks in the array to get
 * the total number of stems available.
 *
 * @param tracks - Array of tracks to count stems for
 * @returns Total number of stems across all tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { nested_stem_count: 4 },
 *   { nested_stem_count: 6 },
 *   { nested_stem_count: 0 }
 * ]
 * getStemsCountFromTracksArray(tracks) // 10
 * ```
 */
export const getStemsCountFromTracksArray = (
  tracks?: Track[] | null,
): number => {
  const stemsCount = (tracks || []).reduce(
    (stemsCount, track) => stemsCount + getStemsCount(track),
    0,
  )

  return stemsCount
}

const emptyArray: Array<{
  ext: string
  title: string
  url: string
}> = []

const formatMapping = {
  optimized: 'Optimised',
  original_else_optimized: 'Original',
} as const

/**
 * Gets download links for ZIP files based on the specified mode.
 *
 * Extracts appropriate ZIP download URLs based on the download mode
 * (original, optimized, or all formats) and formats them for display.
 *
 * @param zip_urls - Object containing ZIP URLs for different formats
 * @param mode - The ZIP download mode (original, optimized, all)
 * @returns Array of download link objects with title, URL, and extension
 *
 * @example
 * ```typescript
 * const zipUrls = {
 *   original: 'https://example.com/original.zip',
 *   optimized: 'https://example.com/optimized.zip'
 * }
 * getZipDownloadsLinks(zipUrls, 'original')
 * // [{ title: 'Original', url: 'https://example.com/original.zip', ext: 'zip' }]
 * ```
 */
export const getZipDownloadsLinks = (
  zip_urls: ZipUrls | null | undefined,
  mode: ZipMode,
): {
  title: string
  url: string
  ext: string
}[] => {
  if (!zip_urls?.[mode]) return emptyArray
  return Object.keys(zip_urls[mode])
    .map(key => ({
      title: formatMapping[key] || '',
      url: zip_urls[mode]?.[key] || '',
      ext: '',
    }))
    .filter(({ url }) => Boolean(url))
}

/**
 * Whether a track has a view page link at all.
 *
 * Same condition as `getViewPageLink`, without building the caption. Call sites
 * that only branch on the link's existence use this so they do not need a
 * translate function.
 *
 * @param track - The track to check
 * @returns True if the track has a catalog or custom page URL
 */
export const hasViewPageLink = (track: Track): boolean =>
  Boolean(track.client_library_url || track.custom_url)

/**
 * Gets the view page link for a track.
 *
 * Generates a link object for viewing the track's dedicated page,
 * including both the URL and display caption.
 *
 * @param t - Translate function bound to the `common` namespace
 * @param track - The track to generate a view page link for
 * @returns Object with URL and caption, or null if no view page available
 */
export const getViewPageLink = (
  t: TranslateFn,
  track: Track,
):
  | {
      url: string
      caption: string
    }
  | null
  | undefined => {
  let result: { url: string; caption: string } | null = null

  if (track.client_library_url) {
    result = {
      url: track.client_library_url,
      caption: t('common:viewCatalog', 'View Catalog'),
    }
  }

  if (track.custom_url) {
    result = {
      url: track.custom_url,
      caption: t('common:viewPage', 'View Page'),
    }
  }

  return result
}

/**
 * Retrieves nested tracks (versions/stems) for a collection of tracks.
 *
 * Asynchronously fetches all nested tracks associated with the provided tracks,
 * including both versions and stems.
 *
 * @param tracks - Array of tracks to get nested tracks for
 * @returns Promise resolving to array of nested tracks
 *
 * @example
 * ```typescript
 * const mainTracks = [{ id: 1, nested_version_count: 2 }]
 * const nestedTracks = await getNestedTracks(mainTracks)
 * // Returns all versions and stems for the main tracks
 * ```
 */
export const getNestedTracks = async (tracks: Track[]): Promise<Track[]> => {
  // Find nested track ids to fetch
  const nestedTrackIds = tracks
    .flatMap(t => t.nested_tracks || [])
    .map(t => t.id)

  if (!nestedTrackIds.length) {
    return []
  }

  // If we have nested tracks, fetch them
  const { tracks: nestedTracksDictionary } = await api.getExistingObjects(
    undefined,
    undefined,
    nestedTrackIds,
  )
  return Object.values(nestedTracksDictionary) as Track[]
}

/**
 * Gets the current status of a track.
 *
 * Returns the track's current processing or display status,
 * such as 'processing', 'ready', 'error', etc.
 *
 * @param track - The track to get status for
 * @returns The track's current status string, or null if no status
 */
export const getTrackStatus = (track: Track): string | null | undefined => {
  let status: string | undefined = undefined
  if (!track.id) {
    if (track.progress === 100) status = 'Processing...'
  } else {
    if (!track.uploaded) status = 'Processing...'
    if (track.transcode_pending) status = 'Transcoding...'
  }
  return status
}

/**
 * Gets the total duration of an array of tracks.
 *
 * Calculates and formats the combined duration of all tracks in the array.
 * Returns a human-readable duration string.
 *
 * @param tracks - Array of tracks to calculate total duration for
 * @returns Formatted duration string, or null if no duration available
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { duration: 180 }, // 3 minutes
 *   { duration: 240 }, // 4 minutes
 *   { duration: 120 }  // 2 minutes
 * ]
 * getTracksDuration(tracks) // '9:00' (9 minutes total)
 * ```
 */
export const getTracksDuration = (
  tracks: (Track | CachedTrack)[],
): string | null | undefined => {
  const duration = tracks.reduce((prev, cur) => {
    return prev + get(cur, 'duration', 0)
  }, 0)

  return duration ? formatDuration(duration) : null
}

/**
 * Builds the track-shaped object an in-flight upload renders through the
 * shared track components (TrackItem/TrackInfo): the temp item's fields plus
 * the display `name` — the (possibly ID3-parsed) title, falling back to the
 * filename — and empty `groups` so group-membership checks work on the row.
 *
 * @param tempItem - The upload's temp item (shortlist or explorer)
 * @returns The temp item as a Track for the shared track components
 */
export const tempItemToTrackRow = (
  tempItem: TempItem | ExplorerUpload,
): Track => {
  const row: Partial<Track> = {
    ...tempItem,
    name: tempItem.title || tempItem.filename,
    groups: [],
  }
  return row as Track
}
